import { ArrowLeftIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline"
import React, { useCallback, useEffect, useRef, useState } from "react"

export type ConversationHistoryItem = {
  id: number
  title?: string | null
  preview: string | null
  updatedAt: string
  messageCount: number
}

type ConversationHistoryJson = {
  conversations: ConversationHistoryItem[]
  hasMore?: boolean
  nextCursor?: string | null
}

export type PortalAiAssistantHistoryDrawerProps = {
  open: boolean
  conversationsUrl: string
  activeConversationId: number | null
  disabled?: boolean
  onClose: () => void
  onSelect: (conversationId: number) => void | Promise<void>
  onDeleted?: (conversationId: number) => void | Promise<void>
}

const PAGE_SIZE = 50

function readCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") ?? ""
}

function formatHistoryDate(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()

    if (sameDay) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    }

    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  } catch {
    return ""
  }
}

function historyLabel(item: ConversationHistoryItem): string {
  const title = item.title?.trim()
  if (title) return title
  const preview = item.preview?.trim()
  if (preview) return preview
  return "Conversation"
}

function conversationUrlFor(baseUrl: string, id: number): string {
  return `${baseUrl.replace(/\/$/, "")}/${id}`
}

export default function PortalAiAssistantHistoryDrawer({
  open,
  conversationsUrl,
  activeConversationId,
  disabled = false,
  onClose,
  onSelect,
  onDeleted
}: PortalAiAssistantHistoryDrawerProps) {
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<ConversationHistoryItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [selectingId, setSelectingId] = useState<number | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null)
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameDraft, setRenameDraft] = useState("")
  const [mutatingId, setMutatingId] = useState<number | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const fetchPage = useCallback(
    async (cursor?: string | null, append = false) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
      if (cursor) params.set("before", cursor)

      const res = await fetch(`${conversationsUrl}?${params.toString()}`, {
        headers: { Accept: "application/json" }
      })

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }

      const data = (await res.json()) as ConversationHistoryJson
      const page = Array.isArray(data.conversations) ? data.conversations : []

      setItems((prev) => (append ? [...prev, ...page] : page))
      setHasMore(Boolean(data.hasMore))
      setNextCursor(data.nextCursor ?? null)
    },
    [conversationsUrl]
  )

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    setHasMore(false)
    setNextCursor(null)
    try {
      await fetchPage(null, false)
    } catch {
      setError("Could not load conversation history.")
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [fetchPage])

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !nextCursor) return
    setLoadingMore(true)
    try {
      await fetchPage(nextCursor, true)
    } catch {
      setError("Could not load more conversations.")
    } finally {
      setLoadingMore(false)
    }
  }, [fetchPage, hasMore, loadingMore, nextCursor])

  useEffect(() => {
    if (!open) return
    setMenuOpenId(null)
    setRenamingId(null)
    void loadHistory()
  }, [open, loadHistory])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (renamingId != null) {
          setRenamingId(null)
          return
        }
        if (menuOpenId != null) {
          setMenuOpenId(null)
          return
        }
        onClose()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [menuOpenId, onClose, open, renamingId])

  useEffect(() => {
    if (!open || !hasMore || loading || loadingMore) return
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          void loadMore()
        }
      },
      { root: null, rootMargin: "100px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMore, loading, loadingMore, open, items.length])

  useEffect(() => {
    if (menuOpenId == null) return
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpenId(null)
      }
    }
    document.addEventListener("mousedown", onDocClick)
    return () => document.removeEventListener("mousedown", onDocClick)
  }, [menuOpenId])

  const handleSelect = useCallback(
    async (conversationId: number) => {
      if (disabled || selectingId != null || mutatingId != null) return
      setSelectingId(conversationId)
      try {
        await onSelect(conversationId)
        onClose()
      } finally {
        setSelectingId(null)
      }
    },
    [disabled, mutatingId, onClose, onSelect, selectingId]
  )

  const handleRenameStart = useCallback((item: ConversationHistoryItem) => {
    setMenuOpenId(null)
    setRenamingId(item.id)
    setRenameDraft(historyLabel(item))
  }, [])

  const handleRenameSubmit = useCallback(
    async (conversationId: number, submittedTitle: string) => {
      const title = submittedTitle.trim()
      setMutatingId(conversationId)
      setError(null)
      try {
        const res = await fetch(conversationUrlFor(conversationsUrl, conversationId), {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": readCsrfToken()
          },
          body: JSON.stringify({ title })
        })

        if (!res.ok) {
          setError("Could not rename conversation.")
          return
        }

        const updated = (await res.json()) as ConversationHistoryItem
        const savedTitle =
          typeof updated.title === "string" && updated.title.trim().length > 0
            ? updated.title.trim()
            : title.length > 0
              ? title
              : null
        setItems((prev) =>
          prev.map((item) =>
            item.id === conversationId
              ? {
                  ...item,
                  title: savedTitle,
                  preview: updated.preview ?? item.preview,
                  updatedAt: updated.updatedAt ?? item.updatedAt,
                  messageCount: updated.messageCount ?? item.messageCount
                }
              : item
          )
        )
        setRenamingId(null)
      } catch {
        setError("Could not rename conversation.")
      } finally {
        setMutatingId(null)
      }
    },
    [conversationsUrl]
  )

  const handleDelete = useCallback(
    async (conversationId: number) => {
      setMenuOpenId(null)
      const label = historyLabel(items.find((i) => i.id === conversationId) ?? { id: conversationId, preview: null, updatedAt: "", messageCount: 0 })
      if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return

      setMutatingId(conversationId)
      try {
        const res = await fetch(conversationUrlFor(conversationsUrl, conversationId), {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "X-CSRF-Token": readCsrfToken()
          }
        })

        if (!res.ok) {
          setError("Could not delete conversation.")
          return
        }

        setItems((prev) => prev.filter((item) => item.id !== conversationId))
        await onDeleted?.(conversationId)
      } catch {
        setError("Could not delete conversation.")
      } finally {
        setMutatingId(null)
      }
    },
    [conversationsUrl, items, onDeleted]
  )

  if (!open) return null

  return (
    <div className="absolute inset-0 z-20 flex flex-col">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/20"
        aria-label="Close conversation history"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Conversation history"
        className="relative z-10 flex h-full min-h-0 w-full max-w-full flex-col bg-white shadow-xl">
        <header className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2.5">
          <button
            type="button"
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100"
            aria-label="Back to chat"
            onClick={onClose}>
            <ArrowLeftIcon className="size-5" aria-hidden />
          </button>
          <h3 className="text-sm font-semibold text-gray-900">Conversation history</h3>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {loading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading history…</p>
          ) : error && items.length === 0 ? (
            <div className="space-y-3 py-6 text-center">
              <p className="text-sm text-red-800">{error}</p>
              <button
                type="button"
                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                onClick={() => void loadHistory()}>
                Try again
              </button>
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No previous conversations yet.</p>
          ) : (
            <>
              {error ? <p className="mb-2 text-center text-xs text-red-700">{error}</p> : null}
              <ul className="space-y-2">
                {items.map((item) => {
                  const isActive = activeConversationId === item.id
                  const isSelecting = selectingId === item.id
                  const isMutating = mutatingId === item.id
                  const isRenaming = renamingId === item.id

                  return (
                    <li key={item.id} className="relative">
                      {isRenaming ? (
                        <form
                          className="rounded-xl border border-teal-500 bg-teal-50/40 px-3 py-2.5"
                          onSubmit={(event) => {
                            event.preventDefault()
                            const form = event.currentTarget
                            const titleInput = form.elements.namedItem("title")
                            const submittedTitle =
                              titleInput instanceof HTMLInputElement ? titleInput.value : renameDraft
                            void handleRenameSubmit(item.id, submittedTitle)
                          }}>
                          <input
                            type="text"
                            name="title"
                            maxLength={120}
                            autoFocus
                            value={renameDraft}
                            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm text-gray-900"
                            aria-label="Conversation title"
                            onChange={(event) => setRenameDraft(event.target.value)}
                          />
                          <div className="mt-2 flex gap-2">
                            <button
                              type="submit"
                              disabled={isMutating}
                              className="rounded-lg bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50">
                              Save
                            </button>
                            <button
                              type="button"
                              disabled={isMutating}
                              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                              onClick={() => setRenamingId(null)}>
                              Cancel
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div
                          className={`flex items-start gap-1 rounded-xl border transition ${
                            isActive ? "border-teal-500 bg-teal-50/60" : "border-gray-200 bg-white"
                          }`}>
                          <button
                            type="button"
                            disabled={disabled || selectingId != null || mutatingId != null}
                            className="min-w-0 flex-1 rounded-xl px-3 py-2.5 text-left hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            onClick={() => void handleSelect(item.id)}>
                            <p className="line-clamp-2 text-sm font-medium text-gray-900">{historyLabel(item)}</p>
                            <p className="mt-1 text-[11px] text-gray-500">
                              {formatHistoryDate(item.updatedAt)}
                              {item.messageCount > 0 ? ` · ${item.messageCount} messages` : null}
                              {isSelecting || isMutating ? " · Loading…" : null}
                            </p>
                          </button>
                          <div className="relative shrink-0 py-1.5 pr-1.5" ref={menuOpenId === item.id ? menuRef : undefined}>
                            <button
                              type="button"
                              disabled={disabled || mutatingId != null}
                              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                              aria-label={`Actions for ${historyLabel(item)}`}
                              aria-expanded={menuOpenId === item.id}
                              onClick={(event) => {
                                event.stopPropagation()
                                setMenuOpenId((prev) => (prev === item.id ? null : item.id))
                              }}>
                              <EllipsisVerticalIcon className="size-4" aria-hidden />
                            </button>
                            {menuOpenId === item.id ? (
                              <div className="absolute right-0 top-full z-10 mt-1 w-36 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                                <button
                                  type="button"
                                  className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    handleRenameStart(item)
                                  }}>
                                  Rename
                                </button>
                                <button
                                  type="button"
                                  className="block w-full px-3 py-1.5 text-left text-xs text-red-700 hover:bg-red-50"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    void handleDelete(item.id)
                                  }}>
                                  Delete
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
              <div ref={sentinelRef} className="h-1" aria-hidden />
              {loadingMore ? (
                <p className="py-3 text-center text-xs text-gray-500">Loading more…</p>
              ) : !hasMore && items.length > 0 ? (
                <p className="py-3 text-center text-xs text-gray-400">End of history</p>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
